import { ArrowRight, Mail, MapPin, MessageCircle } from 'lucide-react';
import { whatsappUrl as whatsappUrlZh, whatsappUrlEn } from '@/lib/whatsapp';
const phoneNumber = '+852 9594 6956';
const emailAddress = 'info@doqcreative.com';

type ContactSectionProps = {
  title: string;
  description: string;
  locale: 'zh' | 'en';
};

export function ContactSection({ title, description, locale }: ContactSectionProps) {
  const isZh = locale === 'zh';
  const whatsappUrl = isZh ? whatsappUrlZh : whatsappUrlEn;
  const address = isZh
    ? '香港北角英皇道367-373號上潤中心19A室'
    : "Room A, 19/F, Max Share Centre, 367-373 King's Road, North Point, Hong Kong";
  const enquiryLabel = isZh ? 'WhatsApp 查詢' : 'WhatsApp enquiry';

  return (
    <section
      id="contact"
      className="relative isolate overflow-hidden bg-[#063b34] px-5 py-12 text-[#fbf5ea] sm:px-8 sm:py-16 lg:px-24 lg:py-[72px]"
    >
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(circle_at_18%_22%,rgba(34,104,84,0.5),transparent_34%),radial-gradient(circle_at_78%_70%,rgba(12,78,68,0.62),transparent_36%),linear-gradient(115deg,#052f2b_0%,#073f36_48%,#052f2f_100%)]" />
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-[0.095] [background-image:linear-gradient(to_right,#f4c06e_1px,transparent_1px),linear-gradient(to_bottom,#f4c06e_1px,transparent_1px)] [background-size:70px_70px]" />
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-[0.05] [background-image:linear-gradient(to_right,#fbf5ea_1px,transparent_1px),linear-gradient(to_bottom,#fbf5ea_1px,transparent_1px)] [background-size:14px_14px]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-[#f4c06e]/35" />

      <div className="relative mx-auto max-w-6xl">
        <div className="max-w-3xl">
          <p className="flex items-center gap-4 text-[13px] font-semibold uppercase tracking-[0.48em] text-[#f4bd70] sm:text-sm">
            <span>Contact</span>
            <span className="h-px w-14 bg-[#f4bd70]" />
          </p>
          <h2 className="mt-5 text-balance font-serif text-[44px] font-semibold leading-[1.04] text-[#fbf5ea] drop-shadow-[0_3px_18px_rgba(0,0,0,0.28)] sm:text-[62px] lg:text-[78px]">
            {title}
          </h2>
          <p className="mt-6 max-w-3xl text-[16px] leading-8 text-[#f5efe3] [text-shadow:0_2px_14px_rgba(0,0,0,0.32)] sm:text-lg sm:leading-9">
            {description}
          </p>
        </div>

        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={enquiryLabel}
          className="mt-8 inline-flex h-12 w-full items-center justify-center gap-3 border border-[#815321] bg-[#815321] px-6 text-base font-semibold text-white shadow-[0_18px_46px_rgba(4,31,27,0.25)] transition duration-300 hover:-translate-y-0.5 hover:bg-[#6f451c] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#f4c06e] active:translate-y-px sm:h-14 sm:text-xl"
        >
          {enquiryLabel}
          <ArrowRight className="size-6" strokeWidth={1.9} />
        </a>

        <div className="mt-7 grid gap-5 md:grid-cols-2 lg:mt-8 lg:grid-cols-4 lg:gap-6">
          <ContactCard
            icon={MessageCircle}
            label="WhatsApp"
            value={phoneNumber}
            href={whatsappUrl}
          />
          <ContactCard icon={MessageCircle} label={isZh ? '電話' : 'Phone'} value={phoneNumber} href="tel:+85295946956" />
          <ContactCard
            icon={Mail}
            label="Email"
            value={emailAddress}
            href={`mailto:${emailAddress}`}
          />
          <ContactCard
            icon={MapPin}
            label={isZh ? '公司地址' : 'Location'}
            value={address}
            multiline
          />
        </div>
      </div>
    </section>
  );
}

function ContactCard({
  icon: Icon,
  label,
  value,
  href,
  multiline = false,
}: {
  icon: typeof MessageCircle;
  label: string;
  value: string;
  href?: string;
  multiline?: boolean;
}) {
  const card = (
    <div className="group flex h-full min-h-[220px] flex-col items-center justify-center border border-[#d9a95b] bg-[#053b34]/42 px-5 py-8 text-center shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_20px_56px_rgba(2,29,25,0.18)] transition duration-300 hover:-translate-y-1 hover:border-[#f4c06e] hover:bg-[#07463d]/66 sm:min-h-[255px] sm:px-7 sm:py-9">
      <Icon className="size-12 text-[#f4bd70] transition duration-300 group-hover:scale-105 sm:size-14" strokeWidth={1.55} />

      <div className="mt-5 flex flex-col items-center sm:mt-6">
        <p className="text-[12px] font-semibold uppercase tracking-[0.44em] text-[#f4bd70]">
          {label}
        </p>
        <span className="mt-4 h-px w-14 bg-[#d9a95b]" />
        <p
          className={[
            'mt-6 font-light text-[#fbf5ea] [text-shadow:0_2px_16px_rgba(0,0,0,0.35)]',
            multiline
              ? 'text-[20px] leading-[1.5] tracking-[0.04em] sm:text-[23px] md:text-[18px] lg:text-[21px] xl:text-[24px]'
              : 'text-[22px] leading-[1.25] tracking-[0.02em] sm:text-[27px] md:text-[19px] lg:text-[23px] xl:text-[26px] [overflow-wrap:anywhere]',
          ].join(' ')}
        >
          {value}
        </p>
      </div>
    </div>
  );

  if (!href) return card;

  return (
    <a
      href={href}
      target={href.startsWith('http') ? '_blank' : undefined}
      rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
      className="block h-full"
    >
      {card}
    </a>
  );
}
